import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, FileText, MailQuestion, ArrowUpRight } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { ADMIN } from '@/lib/adminApi';
import { Skeleton } from '@/components/ui/skeleton';

function StatCard({ icon: Icon, label, total, recent, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{label}</p>
          <p className="mt-1 text-3xl font-bold text-slate-900">{total ?? 0}</p>
        </div>
        <div className="h-10 w-10 rounded-lg bg-[#0A2540]/8 flex items-center justify-center">
          <Icon className="h-5 w-5 text-[#0A2540]" />
        </div>
      </div>
      <div className="mt-3 flex items-center gap-1 text-sm">
        <ArrowUpRight className="h-4 w-4 text-emerald-600" />
        <span className="text-emerald-700 font-semibold">{recent ?? 0}</span>
        <span className="text-slate-500">new this week</span>
      </div>
    </div>
  );
}

const SKELETON_KEYS = ['s1', 's2', 's3'];

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await ADMIN.overview();
      setData(d);
    } catch (err) {
      // Surface load failure so blank dashboard isn't silent
      // eslint-disable-next-line no-console
      console.error('Failed to load admin overview', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{SKELETON_KEYS.map((k) => <Skeleton key={k} className="h-32 rounded-2xl" />)}</div>
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    );
  }

  if (!data) return <div className="text-slate-500">Could not load overview.</div>;

  const stats = data.stats || {};

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <header>
        <h1 className="text-2xl font-bold text-slate-900">Overview</h1>
        <p className="text-sm text-slate-500 mt-1">Real-time pulse of leads, applications, and contact messages.</p>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={Users} label="Total Leads" total={stats.leads?.total} recent={stats.leads?.recent} testid="stat-leads" />
        <StatCard icon={FileText} label="Applications" total={stats.applications?.total} recent={stats.applications?.recent} testid="stat-applications" />
        <StatCard icon={MailQuestion} label="Contact Messages" total={stats.contacts?.total} recent={stats.contacts?.recent} testid="stat-contacts" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Line chart */}
        <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
          <h2 className="text-base font-bold text-slate-900">Submissions by week (last 12 weeks)</h2>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.series || []} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="week_start" stroke="#94a3b8" fontSize={11} tickFormatter={(v) => v?.slice(5)} />
                <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="leads" stroke="#0A2540" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="applications" stroke="#c41e3a" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="contacts" stroke="#0ea5e9" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Lead sources */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
          <h2 className="text-base font-bold text-slate-900">Application sources</h2>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.lead_sources || []} layout="vertical" margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={100} />
                <Tooltip />
                <Bar dataKey="value" fill="#0A2540" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold text-slate-900">Recent activity</h2>
        </div>
        {(!data.recent || data.recent.length === 0) ? (
          <p className="text-sm text-slate-500">No activity yet.</p>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="recent-activity">
            {data.recent.map((r) => (
              <div key={`${r.type}-${r.id || r.name}-${r.created_at || ''}`} className="py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide rounded-full bg-slate-100 text-slate-700">{r.type}</span>
                    <span className="text-sm font-semibold text-slate-900 truncate">{r.name || '—'}</span>
                  </div>
                  <div className="text-xs text-slate-500 truncate">{r.subtitle || ''}</div>
                </div>
                <Link to={`/admin/${r.type === 'lead' ? 'leads' : r.type === 'application' ? 'applications' : 'contacts'}`} className="text-xs font-semibold text-[#0A2540] hover:underline shrink-0">View</Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
